import type { ActionTree } from "vuex";
import type {
  SelfDataState,
  DownloadConfig,
  DownloadRequest,
  RootState,
} from "@/types";
import { exportVariablesTableCsv } from "@/common/csv";
import { dateFormatterGenerator } from "@/common/date";
import { httpClient, type AxiosResponse } from "@/services/httpClient";
import {
  LOAD_VARIABLES,
  LOAD_VARIABLES_SUCCESS,
  LOAD_VARIABLES_FAILURE,
  LOAD_VARIABLES_EXPORT,
  LOAD_VARIABLES_EXPORT_SUCCESS,
  LOAD_VARIABLES_EXPORT_FAILURE,
  LOAD_DOWNLOAD_CONFIGS,
  LOAD_DOWNLOAD_CONFIGS_SUCCESS,
  LOAD_DOWNLOAD_CONFIGS_FAILURE,
  LOAD_FILES,
  LOAD_FILES_SUCCESS,
  LOAD_FILES_FAILURE,
  DELETE_FILES,
  DELETE_FILES_SUCCESS,
  DELETE_FILES_FAILURE,
  SEND_DOWNLOAD_REQUEST,
  SEND_DOWNLOAD_REQUEST_SUCCESS,
  SEND_DOWNLOAD_REQUEST_FAILURE,
  SEND_DOWNLOAD_CONFIGS,
  SEND_DOWNLOAD_CONFIGS_SUCCESS,
  SEND_DOWNLOAD_CONFIGS_FAILURE,
  DELETE_DOWNLOAD_CONFIGS,
  DELETE_DOWNLOAD_CONFIGS_SUCCESS,
  DELETE_DOWNLOAD_CONFIGS_FAILURE,
  UPDATE_DOWNLOAD_CONFIGS,
  UPDATE_DOWNLOAD_CONFIGS_SUCCESS,
  UPDATED_DOWNLOAD_CONFIGS_FAILURE,
  CLEAR_DOWNLOAD_CONFIG_SELF_DATE_STORE,
  CLEAR_FILES_SELF_DATA_STORE,
  LOAD_IMPORTS,
  LOAD_IMPORTS_SUCCESS,
  LOAD_IMPORTS_FAILURE,
  CLEAR_IMPORT_FILE,
  SAVE_IMPORT_FILE,
  EDIT_IMPORTS,
  EDIT_IMPORTS_SUCCESS,
  EDIT_IMPORTS_FAILURE,
  DELETE_IMPORT,
  DELETE_IMPORT_SUCCESS,
  DELETE_IMPORT_FAILURE,
  LOAD_HISTORY_VAR_CHANGE,
  LOAD_LOAD_HISTORY_VAR_CHANGE_SUCCESS,
  LOAD_LOAD_HISTORY_VAR_CHANGE_FAILURE,
} from "./labels";

import { HOSTS, ENDPOINTS } from "@/constants";
import axios from "axios";

export const actions: ActionTree<SelfDataState, RootState> = {
  //VARIABLES
  fetchVariables({ commit }, params: any) {
    commit(LOAD_VARIABLES);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.VARIABLES}`, {
        params: params,
      })
      .then(
        (response: any) => {
          commit(LOAD_VARIABLES_SUCCESS, {
            variables: response.data.map((variable: any) => {
              return { ...variable, aggregationId: variable.aggregation.id };
            }),
            count: response.total_count,
          });
        },
        (error) => {
          commit(LOAD_VARIABLES_FAILURE, error.message);
        },
      );
  },

  fetchVariablesProjectForExport({ commit }, params: any) {
    const { varsListColumns, limit, ...requestParams }: any = params;
    const maxLimit = 500;

    commit(LOAD_VARIABLES_EXPORT);

    const requests = [];
    let offset = 0;
    while (limit - offset > 0) {
      const currentLimit = Math.min(maxLimit, limit - offset);
      requests.push(
        httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.VARIABLES}`, {
          params: {
            ...requestParams,
            limit: currentLimit,
            offset,
          },
        }),
      );
      offset += currentLimit;
    }

    Promise.all(requests)
      .then((responses) => {
        const data = responses.flatMap((response: any) => response.data);
        commit(LOAD_VARIABLES_EXPORT_SUCCESS);

        exportVariablesTableCsv(
          data.map((variable: any) => ({
            Name: variable.name,
            Unit: variable.unit.name,
            Project: variable.projects
              ?.map((project: any) => project.name)
              .join("/"),
            Site: variable.projects[0]?.place.name,
            Client: variable.projects[0]?.place.client.name,
            AcquisitionDevice: variable.acquisitionDevice?.name,
            IdTechnique: variable.sourceTechnicalName,
            Type: variable.type,
            Aggregation: variable.aggregation.name,
            Attached:
              variable.projects && variable.projects.length ? "Oui" : "Non",
          })),
          `variables_list_${dateFormatterGenerator(new Date().toString())}`,
          [...varsListColumns, "attached"],
        );
      })
      .catch((error) => {
        commit(LOAD_VARIABLES_EXPORT_FAILURE, error.message);
      });
  },

  fetchVariablesForExport({ commit }, params: any) {
    const { varsListColumns, limit, ...requestParams }: any = params;
    const maxLimit = 500;

    commit(LOAD_VARIABLES_EXPORT);

    const requests = [];
    let offset = 0;
    while (limit - offset > 0) {
      const currentLimit = Math.min(maxLimit, limit - offset);
      requests.push(
        httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.VARIABLES}`, {
          params: {
            ...requestParams,
            limit: currentLimit,
            offset,
          },
        }),
      );
      offset += currentLimit;
    }

    Promise.all(requests)
      .then((responses) => {
        const data = responses.flatMap((response: any) => response.data);
        commit(LOAD_VARIABLES_EXPORT_SUCCESS);
        exportVariablesTableCsv(
          data.map((variabe: any) => ({
            Name: variabe.name,
            Unit: variabe.unit.name,
            Project: variabe.projects
              ?.map((project: any) => project.name)
              .join("/"),
            Site: variabe.place?.name,
            Client: variabe.client?.name,
            AcquisitionDevice: variabe.acquisitionDevice?.name,
            IdTechnique: variabe.sourceTechnicalName,
            Type: variabe.type,
            Aggregation: variabe.aggregation.name,
          })),
          `variables_list_${dateFormatterGenerator(new Date().toString())}`,
          varsListColumns,
        );
      })
      .catch((error) => {
        commit(LOAD_VARIABLES_EXPORT_FAILURE, error.message);
      });
  },

  //IMPORTS
  fetchImports({ commit }, params: any) {
    commit(LOAD_IMPORTS);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.IMPORTS}`, {
        params: params,
      })
      .then(
        (response: any) => {
          commit(LOAD_IMPORTS_SUCCESS, {
            imports: response.data,
            count: response.total_count,
          });
        },
        (error) => {
          commit(LOAD_IMPORTS_FAILURE, error.message);
        },
      );
  },
  updateImport({ commit }, params: any) {
    commit(EDIT_IMPORTS);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.IMPORTS}/${params.id}`, {
        status: params.status,
        name: params.name,
      })
      .then(
        (response: any) => {
          commit(EDIT_IMPORTS_SUCCESS, response.data);
        },
        (error) => {
          commit(EDIT_IMPORTS_FAILURE, error.message);
        },
      );
  },

  //DOWNLOAD_REQUEST
  sendDownloadRequest({ commit }, downloadRequest: DownloadRequest) {
    commit(SEND_DOWNLOAD_REQUEST);
    httpClient
      .post(`${HOSTS.AWS}/${ENDPOINTS.DOWNLOAD_REQUEST}`, downloadRequest)
      .then(
        () => {
          commit(SEND_DOWNLOAD_REQUEST_SUCCESS);
        },
        (error) => {
          commit(SEND_DOWNLOAD_REQUEST_FAILURE, error.message);
        },
      );
  },

  // DOWNLOAD_CONFIGS
  sendDownloadConfig({ commit }, downloadConfig: DownloadConfig) {
    commit(SEND_DOWNLOAD_CONFIGS);
    httpClient
      .post(`${HOSTS.AWS}/${ENDPOINTS.DOWNLOAD_CONFIGS}`, downloadConfig)
      .then(
        (response: AxiosResponse<DownloadConfig>) => {
          commit(SEND_DOWNLOAD_CONFIGS_SUCCESS, response.data);
        },
        (error) => {
          commit(SEND_DOWNLOAD_CONFIGS_FAILURE, error.message);
        },
      );
  },

  fetchDownloadConfigByProjectId({ commit }, projectId?: string) {
    commit(LOAD_DOWNLOAD_CONFIGS);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.DOWNLOAD_CONFIGS}`, {
        params: { projectId },
      })
      .then(
        (response: AxiosResponse<DownloadConfig[]>) => {
          commit(LOAD_DOWNLOAD_CONFIGS_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD_DOWNLOAD_CONFIGS_FAILURE, error.message);
        },
      );
  },
  fetchDowloadConfig({ commit }, params: any) {
    commit(LOAD_DOWNLOAD_CONFIGS);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.DOWNLOAD_CONFIGS}`, {
        params: params,
      })
      .then(
        (response: AxiosResponse<DownloadConfig[]>) => {
          commit(LOAD_DOWNLOAD_CONFIGS_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD_DOWNLOAD_CONFIGS_FAILURE, error.message);
        },
      );
  },

  deleteDownloadConfig({ commit }, downloadConfigId: string) {
    commit(DELETE_DOWNLOAD_CONFIGS);
    httpClient
      .delete(`${HOSTS.AWS}/${ENDPOINTS.DOWNLOAD_CONFIGS}/${downloadConfigId}`)
      .then(
        () => {
          commit(DELETE_DOWNLOAD_CONFIGS_SUCCESS, downloadConfigId);
        },
        (error) => {
          commit(DELETE_DOWNLOAD_CONFIGS_FAILURE, error.message);
        },
      );
  },
  updateDownloadConfig({ commit }, downloadConfig: DownloadConfig) {
    commit(UPDATE_DOWNLOAD_CONFIGS);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.DOWNLOAD_CONFIGS}/${downloadConfig.id}`, {
        ...downloadConfig,
        downloadConfigId: undefined,
      })
      .then(
        () => {
          commit(UPDATE_DOWNLOAD_CONFIGS_SUCCESS, downloadConfig);
        },
        (error) => {
          commit(UPDATED_DOWNLOAD_CONFIGS_FAILURE, error.message);
        },
      );
  },

  fetchFilesByProjectId({ commit }, projectId?: string) {
    commit(LOAD_FILES);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.FILES}`, {
        params: { projectId },
      })
      .then(
        (response: AxiosResponse<DownloadRequest[]>) => {
          commit(LOAD_FILES_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD_FILES_FAILURE, error.message);
        },
      );
  },
  fetchFiles({ commit }, params: any) {
    commit(LOAD_FILES);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.FILES}`, {
        params: params,
      })
      .then(
        (response: AxiosResponse<DownloadRequest[]>) => {
          commit(LOAD_FILES_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD_FILES_FAILURE, error.message);
        },
      );
  },
  downloadFiles({ commit }, downloadRequestId: string) {
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.FILES}/${downloadRequestId}/download`)
      .then(
        (response: AxiosResponse) => {
          window.location.assign(response.data.url);
        },
        (error) => {
          commit(LOAD_FILES_FAILURE, error.message);
        },
      );
  },
  deleteFiles({ commit }, deletedFile: DownloadRequest) {
    commit(DELETE_FILES);
    httpClient
      .delete(`${HOSTS.AWS}/${ENDPOINTS.FILES}/${deletedFile?.id}`)
      .then(
        () => {
          commit(DELETE_FILES_SUCCESS, deletedFile);
        },
        (error) => {
          commit(DELETE_FILES_FAILURE, error.message);
        },
      );
  },
  //CLEAR_DOWNLOAD_CONFIG_SELF_DATE_STORE
  clearDownloadConfigSelfDataStore({ commit }) {
    commit(CLEAR_DOWNLOAD_CONFIG_SELF_DATE_STORE);
  },
  clearFilesSekfDataStore({ commit }) {
    commit(CLEAR_FILES_SELF_DATA_STORE);
  },

  saveImportFile({ commit }, file: any) {
    commit(SAVE_IMPORT_FILE, file);
  },

  updateImportFile({ commit, state }, url: string) {
    const file = state.currentFileImport;
    axios
      .put(`${url}`, file, {
        headers: {
          "Content-Type": "application/octet-stream",
        },
      })
      .then(
        () => {
          commit(CLEAR_IMPORT_FILE);
        },
        (error) => {
          console.error(error);
        },
      );
  },

  deleteImport({ commit }, importId: string) {
    commit(DELETE_IMPORT);
    httpClient.delete(`${HOSTS.AWS}/${ENDPOINTS.IMPORTS}/${importId}`).then(
      () => {
        commit(DELETE_IMPORT_SUCCESS, importId);
      },
      (error) => {
        commit(DELETE_IMPORT_FAILURE, error.message);
      },
    );
  },

  //VARIABLES
  fetchHistoryVariablesChange({ commit }, params: any) {
    commit(LOAD_HISTORY_VAR_CHANGE);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.VARIABLES}/operations`, {
        params: params,
      })
      .then(
        (response: any) => {
          commit(LOAD_LOAD_HISTORY_VAR_CHANGE_SUCCESS, response);
        },
        (error) => {
          commit(LOAD_LOAD_HISTORY_VAR_CHANGE_FAILURE, error.message);
        },
      );
  },
};
